import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Download, FileText, AlertCircle, Loader2, CheckCircle, XCircle, ExternalLink, Archive } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HECDocumentsPage() {
  const { planningId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!planningId || !token) {
        setError("Lien invalide - paramètres manquants");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/hec/plannings/${planningId}/documents-pack?token=${token}`
        );
        setData(response.data);
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError(err.response?.data?.detail || "Lien invalide ou expiré");
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [planningId, token]);

  const handleDownloadAllZip = async () => {
    if (!planningId || !token) return;
    
    setDownloadingZip(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/hec/plannings/${planningId}/download-all-docs?token=${token}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Documents_HEC.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading ZIP:", err);
      alert("Erreur lors du téléchargement. Veuillez réessayer.");
    } finally {
      setDownloadingZip(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#8B1538] mx-auto mb-4" />
          <p className="text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group documents by food truck
  const groupedDocs = {};
  data?.documents?.forEach((doc) => {
    if (!groupedDocs[doc.food_truck]) {
      groupedDocs[doc.food_truck] = [];
    }
    groupedDocs[doc.food_truck].push(doc);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B1538] to-[#6B1028] text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">Food Truck United</h1>
          <p className="opacity-90 mt-1">Documents - {data?.planning_name || "Planning HEC"}</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#8B1538]" />
              Documents disponibles
            </CardTitle>
            <CardDescription>
              {data?.documents_count || 0} document(s) disponible(s) au téléchargement
            </CardDescription>
          </CardHeader>
        </Card>

        {Object.keys(groupedDocs).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun document disponible</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {Object.entries(groupedDocs).map(([ftName, docs]) => (
              <Card key={ftName}>
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="text-lg">{ftName}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {docs.map((doc, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{doc.type}</p>
                            <p className="text-sm text-gray-500">{doc.filename}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(doc.url, "_blank")}
                          className="gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Télécharger
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Download all section */}
        {data?.documents_count > 0 && (
          <Card className="mt-6 border-2 border-[#8B1538]">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#8B1538]/10 flex items-center justify-center">
                    <Archive className="w-6 h-6 text-[#8B1538]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">Télécharger tous les documents</h3>
                    <p className="text-sm text-gray-600">
                      {data?.documents_count} document(s) dans un seul fichier ZIP
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleDownloadAllZip}
                  disabled={downloadingZip}
                  className="bg-[#8B1538] hover:bg-[#6B1028] text-white gap-2 min-w-[200px]"
                  data-testid="download-all-zip-btn"
                >
                  {downloadingZip ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Préparation...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Télécharger tout (.zip)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-3">Types de documents:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Pièce d'identité</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Assurance RC Pro</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>HACCP</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Assurance Véhicule</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Food Truck United - Plateforme de gestion</p>
          <p className="mt-1">Ce lien est valide pendant 7 jours</p>
        </div>
      </div>
    </div>
  );
}
